import { today } from '@internationalized/date';
import { isEqual, noop } from 'lodash';
import { Settings } from 'luxon';
import { memo, useMemo } from 'react';

import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { CaseModule, CaseState, ChecklistItem } from 'dashboard/models/case-template';
import { CaseVariableDefinition } from 'dashboard/models/case-variable-definition';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Template } from 'dashboard/models/template';
import { CaseModuleBase } from 'dashboard/modules/CaseModuleBase/CaseModuleBase';
import { createPreviewAccount } from 'dashboard/modules/CaseModuleBase/previewBuilders/account';
import { createPreviewAccountStatus } from 'dashboard/modules/CaseModuleBase/previewBuilders/account-status';
import { createPreviewAccountType } from 'dashboard/modules/CaseModuleBase/previewBuilders/account-type';
import { createPreviewCase } from 'dashboard/modules/CaseModuleBase/previewBuilders/case';
import { createPreviewCaseComment } from 'dashboard/modules/CaseModuleBase/previewBuilders/case-comment';
import { createPreviewDevice } from 'dashboard/modules/CaseModuleBase/previewBuilders/device';
import { createPreviewDocumentGeneric } from 'dashboard/modules/CaseModuleBase/previewBuilders/document';
import { createPreviewEnvironmentProduction } from 'dashboard/modules/CaseModuleBase/previewBuilders/environment';
import { createPreviewInquiry } from 'dashboard/modules/CaseModuleBase/previewBuilders/inquiry';
import { createPreviewInquirySession } from 'dashboard/modules/CaseModuleBase/previewBuilders/inquiry-session';
import { createPreviewInquiryTemplate } from 'dashboard/modules/CaseModuleBase/previewBuilders/inquiry-template';
import {
  createPreviewReportAddressLookup,
  createPreviewReportAdverseMedia,
  createPreviewReportBusinessAdverseMedia,
  createPreviewReportBusinessAssociatedPersons,
  createPreviewReportBusinessClassification,
  createPreviewReportBusinessIndustryClassification,
  createPreviewReportBusinessLiensLookup,
  createPreviewReportBusinessLookup,
  createPreviewReportBusinessNonprofit,
  createPreviewReportBusinessOnlinePresence,
  createPreviewReportBusinessRegistrationsLookup,
  createPreviewReportBusinessSocialMedia,
  createPreviewReportBusinessWatchlist,
  createPreviewReportBusinessWebsite,
  createPreviewReportChainalysisAddressScreening,
  createPreviewReportCryptoAddressWatchlist,
  createPreviewReportCustomList,
  createPreviewReportEmailAddress,
  createPreviewReportFinraBrokerCheck,
  createPreviewReportMxAccount,
  createPreviewReportPhoneNumber,
  createPreviewReportPoliticallyExposedPerson,
  createPreviewReportProfile,
  createPreviewReportProfileNonAuthoritative,
  createPreviewReportSecActionLookup,
  createPreviewReportSentilinkApplicationRisk,
  createPreviewReportSentilinkScores,
  createPreviewReportSocialMedia,
  createPreviewReportSynthetic,
  createPreviewReportWatchlist,
} from 'dashboard/modules/CaseModuleBase/previewBuilders/report';
import { createPreviewReportRunCustomList } from 'dashboard/modules/CaseModuleBase/previewBuilders/report-runs';
import {
  createPreviewReportTemplateAddressLookup,
  createPreviewReportTemplateAdverseMedia,
  createPreviewReportTemplateBusinessAdverseMedia,
  createPreviewReportTemplateBusinessAssociatedPersons,
  createPreviewReportTemplateBusinessClassification,
  createPreviewReportTemplateBusinessIndustryClassification,
  createPreviewReportTemplateBusinessLiensLookup,
  createPreviewReportTemplateBusinessLookup,
  createPreviewReportTemplateBusinessNonprofit,
  createPreviewReportTemplateBusinessOnlinePresence,
  createPreviewReportTemplateBusinessRegistrationsLookup,
  createPreviewReportTemplateBusinessSocialMedia,
  createPreviewReportTemplateBusinessWatchlist,
  createPreviewReportTemplateBusinessWebsite,
  createPreviewReportTemplateChainalysisAddressScreening,
  createPreviewReportTemplateCryptoAddressWatchlist,
  createPreviewReportTemplateCustomList,
  createPreviewReportTemplateEmailAddress,
  createPreviewReportTemplateFinraBrokerCheck,
  createPreviewReportTemplateMxAccount,
  createPreviewReportTemplatePhoneNumber,
  createPreviewReportTemplatePoliticallyExposedPerson,
  createPreviewReportTemplateProfile,
  createPreviewReportTemplateProfileNonAuthoritative,
  createPreviewReportTemplateSecActionLookup,
  createPreviewReportTemplateSentilinkApplicationRisk,
  createPreviewReportTemplateSentilinkScores,
  createPreviewReportTemplateSocialMedia,
  createPreviewReportTemplateSynthetic,
  createPreviewReportTemplateWatchlist,
} from 'dashboard/modules/CaseModuleBase/previewBuilders/report-template';
import { createPreviewTag } from 'dashboard/modules/CaseModuleBase/previewBuilders/tag';
import { createPreviewTransactionStatus } from 'dashboard/modules/CaseModuleBase/previewBuilders/transaction-status';
import { createPreviewTransactionType } from 'dashboard/modules/CaseModuleBase/previewBuilders/transaction-type';
import { createPreviewTransaction } from 'dashboard/modules/CaseModuleBase/previewBuilders/transactions';
import { createPreviewUser } from 'dashboard/modules/CaseModuleBase/previewBuilders/user';
import {
  createPreviewVerificationAamva,
  createPreviewVerificationBusinessWebsite,
  createPreviewVerificationDatabase,
  createPreviewVerificationDatabaseBusiness,
  createPreviewVerificationDatabaseEcbsv,
  createPreviewVerificationDatabasePhoneCarrier,
  createPreviewVerificationDatabaseStandard,
  createPreviewVerificationDatabaseTin,
  createPreviewVerificationDatabaseVatNumber,
  createPreviewVerificationDigitalIdUkSharecode,
  createPreviewVerificationDocument,
  createPreviewVerificationEmailAddress,
  createPreviewVerificationGovernmentId,
  createPreviewVerificationPhoneNumber,
  createPreviewVerificationSelfie,
} from 'dashboard/modules/CaseModuleBase/previewBuilders/verification';
import EventTimeline from 'dashboard/modules/EventTimeline';
import { PureRelatedCasesModule } from 'dashboard/modules/RelatedCases';
import TablePreview from 'dashboard/modules/Table/TablePreview';
import { useCaseDataContext } from 'dashboard/routes/DashboardCaseShowRoute/useCaseDataContext';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import { FieldSchema, FieldSchemaType, FieldType } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';

const currentEnvironment = createPreviewEnvironmentProduction();

interface CaseModuleBasePreviewProps {
  fieldSchema: FieldSchema[];
  checklistItems: ChecklistItem[];
  enabledInquiryTemplates: (InquiryTemplate | Template)[];
  caseTemplateStates: CaseState[];
  caseVariableDefinitions: CaseVariableDefinition[];
  module: CaseModule;
}

const CaseModuleBasePreview = ({
  fieldSchema,
  checklistItems,
  enabledInquiryTemplates,
  caseTemplateStates,
  caseVariableDefinitions,
  module,
}: CaseModuleBasePreviewProps) => {
  const user = createPreviewUser();
  const filledFields: Record<string, any> = getFilledFields(fieldSchema);
  const highRiskTag = createPreviewTag({ name: 'HIGH RISK' });
  const largeForeignTransactionTag = createPreviewTag({ name: 'LARGE FOREIGN TRANSACTION' });
  const largeACHReversalTag = createPreviewTag({ name: 'LARGE ACH REVERSAL' });

  const caseTags = [createPreviewTag({ name: 'tag 1' }), createPreviewTag({ name: 'tag 2' })];
  const transactionTags = [highRiskTag, largeForeignTransactionTag, largeACHReversalTag];
  const allTags = [...caseTags, ...transactionTags];

  const caseChecklistItems = checklistItems.map((item) => {
    return {
      token: item.token,
      description: item.description,
      'completed-at': null,
      'required-for': item['required-for'],
    };
  });

  const kase = createPreviewCase(
    {
      fields: filledFields,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'checklist-items': caseChecklistItems,
    },
    {
      assignee: { data: { id: user.id, type: ApiModel.User } },
      tags: {
        data: caseTags.map((tag) => ({ id: tag.id, type: tag.type })),
      },
    },
  );

  useCaseDataContext({
    caseId: kase.id,
    variableDefinitions: caseVariableDefinitions,
    isPreview: true,
  });

  const caseComments = [
    createPreviewCaseComment({}, { commenter: { data: { id: user.id, type: user.type } } }),
  ];

  const document = createPreviewDocumentGeneric();
  const verificationGovId = createPreviewVerificationGovernmentId();
  const verificationSelfie = createPreviewVerificationSelfie();

  // Create 2 attempts for each type of verification.
  const verifications = [
    ...[verificationGovId, createPreviewVerificationGovernmentId()],
    ...[verificationSelfie, createPreviewVerificationSelfie()],
    ...[createPreviewVerificationDocument(), createPreviewVerificationDocument()],
    ...[createPreviewVerificationDatabase(), createPreviewVerificationDatabase()],
    ...[createPreviewVerificationDatabaseStandard(), createPreviewVerificationDatabaseStandard()],
    ...[createPreviewVerificationDatabaseBusiness(), createPreviewVerificationDatabaseBusiness()],
    ...[createPreviewVerificationAamva(), createPreviewVerificationAamva()],
    ...[createPreviewVerificationDatabaseEcbsv(), createPreviewVerificationDatabaseEcbsv()],
    ...[
      createPreviewVerificationDatabasePhoneCarrier(),
      createPreviewVerificationDatabasePhoneCarrier(),
    ],
    ...[createPreviewVerificationDatabaseTin(), createPreviewVerificationDatabaseTin()],
    ...[createPreviewVerificationDatabaseVatNumber(), createPreviewVerificationDatabaseVatNumber()],
    ...[createPreviewVerificationEmailAddress(), createPreviewVerificationEmailAddress()],
    ...[createPreviewVerificationPhoneNumber(), createPreviewVerificationPhoneNumber()],
    ...[createPreviewVerificationBusinessWebsite(), createPreviewVerificationBusinessWebsite()],
    ...[
      createPreviewVerificationDigitalIdUkSharecode(),
      createPreviewVerificationDigitalIdUkSharecode(),
    ],
  ];

  const device = createPreviewDevice();
  const sessions = [
    createPreviewInquirySession(
      {},
      {
        device: { data: { id: device.id, type: ApiModel.Device } },
      },
    ),
  ];

  const inquiryTag = createPreviewTag({ name: 'inquiry tag' });
  const inquiryTemplate = createPreviewInquiryTemplate();
  const previewTemplateInquiries = [
    createPreviewInquiry(
      {},
      {
        'inquiry-template': {
          data: { id: inquiryTemplate.id, type: ApiModel.InquiryTemplate },
        },
        tags: { data: [{ id: inquiryTag.id, type: ApiModel.Tag }] },
        documents: { data: [{ id: document.id, type: ApiModel.DocumentGeneric }] },
      },
    ),
  ];
  const perTemplateInquiries = enabledInquiryTemplates.map((it) =>
    createPreviewInquiry(
      {},
      {
        'inquiry-template': { data: { id: it.id, type: ApiModel.InquiryTemplate } },
        tags: { data: [{ id: inquiryTag.id, type: ApiModel.Tag }] },
        documents: { data: [{ id: document.id, type: ApiModel.DocumentGeneric }] },
        verifications: {
          data: [
            { id: verificationGovId.id, type: ApiModel.VerificationGovernmentId },
            { id: verificationSelfie.id, type: ApiModel.VerificationSelfie },
          ],
        },
      },
    ),
  );
  const inquiries =
    enabledInquiryTemplates.length > 0 ? perTemplateInquiries : previewTemplateInquiries;
  const inquiryTemplates =
    enabledInquiryTemplates.length > 0 ? enabledInquiryTemplates : [inquiryTemplate];

  const reportTemplateAddressLookup = createPreviewReportTemplateAddressLookup();
  const reportAddressLookup = createPreviewReportAddressLookup(
    {},
    {
      'report-template': {
        data: { id: reportTemplateAddressLookup.id, type: ApiModel.ReportTemplateAddressLookup },
      },
    },
  );

  const reportTemplateAdverseMedia = createPreviewReportTemplateAdverseMedia();
  const reportAdverseMedia = createPreviewReportAdverseMedia(
    {},
    {
      'report-template': {
        data: { id: reportTemplateAdverseMedia.id, type: ApiModel.ReportTemplateAdverseMedia },
      },
    },
  );

  const reportTemplateBusinessAdverseMedia = createPreviewReportTemplateBusinessAdverseMedia();
  const reportBusinessAdverseMedia = createPreviewReportBusinessAdverseMedia(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessAdverseMedia.id,
          type: ApiModel.ReportTemplateBusinessAdverseMedia,
        },
      },
    },
  );

  const reportTemplateBusinessAssociatedPersons =
    createPreviewReportTemplateBusinessAssociatedPersons();
  const reportBusinessAssociatedPersons = createPreviewReportBusinessAssociatedPersons(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessAssociatedPersons.id,
          type: ApiModel.ReportTemplateBusinessAssociatedPersons,
        },
      },
    },
  );

  const ReportTemplateBusinessClassification = createPreviewReportTemplateBusinessClassification();
  const reportBusinessClassification = createPreviewReportBusinessClassification(
    {},
    {
      'report-template': {
        data: {
          id: ReportTemplateBusinessClassification.id,
          type: ApiModel.ReportTemplateBusinessClassification,
        },
      },
    },
  );

  const ReportTemplateBusinessIndustryClassification =
    createPreviewReportTemplateBusinessIndustryClassification();
  const reportBusinessIndustryClassification = createPreviewReportBusinessIndustryClassification(
    {},
    {
      'report-template': {
        data: {
          id: ReportTemplateBusinessIndustryClassification.id,
          type: ApiModel.ReportTemplateBusinessIndustryClassification,
        },
      },
    },
  );

  const reportTemplateBusinessLiensLookup = createPreviewReportTemplateBusinessLiensLookup();
  const reportBusinessLiensLookup = createPreviewReportBusinessLiensLookup(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessLiensLookup.id,
          type: ApiModel.ReportTemplateBusinessLiensLookup,
        },
      },
    },
  );

  const reportTemplateBusinessLookup = createPreviewReportTemplateBusinessLookup();
  const reportBusinessLookup = createPreviewReportBusinessLookup(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessLookup.id,
          type: ApiModel.ReportTemplateBusinessLookup,
        },
      },
    },
  );

  const reportTemplateBusinessNonprofit = createPreviewReportTemplateBusinessNonprofit();
  const reportBusinessNonprofit = createPreviewReportBusinessNonprofit(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessNonprofit.id,
          type: ApiModel.ReportTemplateBusinessNonprofit,
        },
      },
    },
  );

  const reportTemplateBusinessOnlinePresence = createPreviewReportTemplateBusinessOnlinePresence();
  const reportBusinessOnlinePresence = createPreviewReportBusinessOnlinePresence(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessOnlinePresence.id,
          type: ApiModel.ReportTemplateBusinessOnlinePresence,
        },
      },
    },
  );

  const reportTemplateBusinessRegistrations =
    createPreviewReportTemplateBusinessRegistrationsLookup();
  const reportBusinessRegistrationsLookup = createPreviewReportBusinessRegistrationsLookup(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessRegistrations.id,
          type: ApiModel.ReportTemplateBusinessRegistrationsLookup,
        },
      },
    },
  );

  const reportTemplateBusinessSocialMedia = createPreviewReportTemplateBusinessSocialMedia();
  const reportBusinessSocialMedia = createPreviewReportBusinessSocialMedia(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessSocialMedia.id,
          type: ApiModel.ReportTemplateBusinessSocialMedia,
        },
      },
    },
  );

  const reportTemplateBusinessWatchlist = createPreviewReportTemplateBusinessWatchlist();
  const reportBusinessWatchlist = createPreviewReportBusinessWatchlist(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessWatchlist.id,
          type: ApiModel.ReportTemplateBusinessWatchlist,
        },
      },
    },
  );

  const reportTemplateBusinessWebsite = createPreviewReportTemplateBusinessWebsite();
  const reportBusinessWebsite = createPreviewReportBusinessWebsite(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateBusinessWebsite.id,
          type: ApiModel.ReportTemplateBusinessWebsite,
        },
      },
    },
  );

  const reportTemplateChainalysisAddressScreening =
    createPreviewReportTemplateChainalysisAddressScreening();
  const reportChainalysisAddressScreening = createPreviewReportChainalysisAddressScreening(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateChainalysisAddressScreening.id,
          type: ApiModel.ReportTemplateChainalysisAddressScreening,
        },
      },
    },
  );

  const reportTemplateCryptoAddressWatchlist = createPreviewReportTemplateCryptoAddressWatchlist();
  const reportCryptoAddressWatchlist = createPreviewReportCryptoAddressWatchlist(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateCryptoAddressWatchlist.id,
          type: ApiModel.ReportTemplateCryptoAddressWatchlist,
        },
      },
    },
  );

  const reportTemplateEmailAddress = createPreviewReportTemplateEmailAddress();
  const reportEmailAddress = createPreviewReportEmailAddress(
    {},
    {
      'report-template': {
        data: { id: reportTemplateEmailAddress.id, type: ApiModel.ReportTemplateEmailAddress },
      },
    },
  );

  const reportTemplateFinraBrokerCheck = createPreviewReportTemplateFinraBrokerCheck();
  const reportFinraBrokerCheck = createPreviewReportFinraBrokerCheck(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateFinraBrokerCheck.id,
          type: ApiModel.ReportTemplateFinraBrokerCheck,
        },
      },
    },
  );

  const reportTemplateMxAccount = createPreviewReportTemplateMxAccount();
  const reportMxAccount = createPreviewReportMxAccount(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateMxAccount.id,
          type: ApiModel.ReportTemplateMxAccount,
        },
      },
    },
  );

  const reportTemplatePhoneNumber = createPreviewReportTemplatePhoneNumber();
  const reportPhoneNumber = createPreviewReportPhoneNumber(
    {},
    {
      'report-template': {
        data: { id: reportTemplatePhoneNumber.id, type: ApiModel.ReportTemplatePhoneNumber },
      },
    },
  );

  const reportTemplateProfile = createPreviewReportTemplateProfile();
  const reportProfile = createPreviewReportProfile(
    {},
    {
      'report-template': {
        data: { id: reportTemplateProfile.id, type: ApiModel.ReportTemplateProfile },
      },
    },
  );

  const reportTemplateProfileNonAuthoritative =
    createPreviewReportTemplateProfileNonAuthoritative();
  const reportProfileNonAuthoritative = createPreviewReportProfileNonAuthoritative(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateProfileNonAuthoritative.id,
          type: ApiModel.ReportTemplateProfileNonAuthoritative,
        },
      },
    },
  );

  const reportTemplatePoliticallyExposedPerson =
    createPreviewReportTemplatePoliticallyExposedPerson();
  const reportPoliticallyExposedPerson = createPreviewReportPoliticallyExposedPerson(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplatePoliticallyExposedPerson.id,
          type: ApiModel.ReportTemplatePoliticallyExposedPerson,
        },
      },
    },
  );

  const reportTemplateSecActionLookup = createPreviewReportTemplateSecActionLookup();
  const reportSecActionLookup = createPreviewReportSecActionLookup(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateSecActionLookup.id,
          type: ApiModel.ReportTemplateSecActionLookup,
        },
      },
    },
  );

  const reportTemplateSentilinkApplicationRisk =
    createPreviewReportTemplateSentilinkApplicationRisk();
  const reportSentilinkApplicationRisk = createPreviewReportSentilinkApplicationRisk(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateSentilinkApplicationRisk.id,
          type: ApiModel.ReportTemplateSentilinkApplicationRisk,
        },
      },
    },
  );

  const reportTemplateSentilinkScores = createPreviewReportTemplateSentilinkScores();
  const reportSentilinkScores = createPreviewReportSentilinkScores(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateSentilinkScores.id,
          type: ApiModel.ReportTemplateSentilinkScores,
        },
      },
    },
  );

  const reportTemplateSocialMedia = createPreviewReportTemplateSocialMedia();
  const reportSocialMedia = createPreviewReportSocialMedia(
    {},
    {
      'report-template': {
        data: { id: reportTemplateSocialMedia.id, type: ApiModel.ReportTemplateSocialMedia },
      },
    },
  );

  const reportTemplateSynthetic = createPreviewReportTemplateSynthetic();
  const reportSynthetic = createPreviewReportSynthetic(
    {},
    {
      'report-template': {
        data: { id: reportTemplateSynthetic.id, type: ApiModel.ReportTemplateSynthetic },
      },
    },
  );

  const reportTemplateWatchlist = createPreviewReportTemplateWatchlist();
  const reportWatchlist = createPreviewReportWatchlist(
    {},
    {
      'report-template': {
        data: { id: reportTemplateWatchlist.id, type: ApiModel.ReportTemplateWatchlist },
      },
    },
  );

  const reportTemplateCustomList = createPreviewReportTemplateCustomList();
  const reportCustomList = createPreviewReportCustomList(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateCustomList.id,
          type: ApiModel.ReportTemplateCustomList,
        },
      },
    },
  );
  const reportRunCustomList = createPreviewReportRunCustomList(
    {},
    {
      'report-template': {
        data: {
          id: reportTemplateCustomList.id,
          type: ApiModel.ReportTemplateCustomList,
        },
      },
    },
  );

  const accountStatus = createPreviewAccountStatus();
  const accountStatuses = [accountStatus];
  const accountType = createPreviewAccountType();

  const legacyUserAccountType = createPreviewAccountType({
    'is-legacy-account-type': true,
    'field-schemas': [
      {
        key: 'name',
        label: '',
        type: FieldSchemaType.Hash,
        config: {
          'item-schemas': [
            {
              key: 'first',
              label: '',
              type: FieldSchemaType.String,
              config: {},
            },
            {
              key: 'middle',
              label: '',
              type: FieldSchemaType.String,
              config: {},
            },
            {
              key: 'last',
              label: '',
              type: FieldSchemaType.String,
              config: {},
            },
          ],
        },
      },
      {
        key: 'address',
        label: '',
        type: FieldSchemaType.Hash,
        config: {
          'item-schemas': [
            {
              key: 'street_1',
              label: '',
              type: FieldSchemaType.String,
              config: {},
            },
            {
              key: 'street_2',
              label: '',
              type: FieldSchemaType.String,
              config: {},
            },
            {
              key: 'subdivision',
              label: '',
              type: FieldSchemaType.String,
              config: {},
            },
            {
              key: 'city',
              label: '',
              type: FieldSchemaType.String,
              config: {},
            },
            {
              key: 'postal_code',
              label: '',
              type: FieldSchemaType.String,
              config: {},
            },
            {
              key: 'country_code',
              label: '',
              type: FieldSchemaType.String,
              config: {},
            },
          ],
        },
      },
      {
        key: 'birthdate',
        label: '',
        type: FieldSchemaType.Date,
        config: {},
      },
      {
        key: 'phone_number',
        label: '',
        type: FieldSchemaType.String,
        config: {},
      },
      {
        key: 'email_address',
        label: '',
        type: FieldSchemaType.String,
        config: {},
      },
    ],
  });
  const legacyUserAccount = createPreviewAccount(
    {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'selfie-photo-url': null,
      'similar-accounts-by-selfie': [
        {
          id: 'act_234',
          'reference-id': 'sw:ep_2',
          confidence: 0.9,
          name: 'Jean Doe',
        },
      ],
    },
    {
      inquiries: {
        data: inquiries.map((inq) => {
          return { id: inq.id, type: ApiModel.Inquiry };
        }),
      },
      reports: { data: [{ id: reportWatchlist.id, type: reportWatchlist.type }] },
      'account-status': { data: accountStatus },
      'account-type': { data: legacyUserAccountType },
    },
  );

  const accountTypes = [legacyUserAccountType, accountType];
  const accounts = [
    legacyUserAccount,
    createPreviewAccount(
      {
        fields: {
          first_name: {
            type: FieldType.String,
            value: 'Jane',
          },
          last_name: {
            type: FieldType.String,
            value: 'Doe',
          },
          product_lines: {
            type: FieldType.MultiChoices,
            value: ['payroll', 'instant_deposit'],
          },
          geo: {
            type: FieldType.Choices,
            value: 'ca',
          },
        },
      },
      {
        'account-status': { data: accountStatus },
        'account-type': { data: accountType },
      },
      'act_246',
    ),
  ];

  const transactionStatus = createPreviewTransactionStatus();
  const transactionStatuses = [transactionStatus];
  const transactionType = createPreviewTransactionType();
  const transactionTypes = [transactionType];

  const transactions = [
    createPreviewTransaction(
      {},
      {
        tags: { data: [highRiskTag] },
        'transaction-status': { data: transactionStatus },
        'transaction-type': { data: transactionType },
      },
    ),
    createPreviewTransaction(
      {},
      {
        tags: { data: [highRiskTag, largeForeignTransactionTag] },
        'transaction-status': { data: transactionStatus },
        'transaction-type': { data: transactionType },
      },
    ),
    createPreviewTransaction(
      {},
      {
        tags: { data: [largeACHReversalTag] },
        'transaction-status': { data: transactionStatus },
        'transaction-type': { data: transactionType },
      },
    ),
  ];

  const caseObjects = [
    ...inquiries,
    ...transactions,
    ...verifications,
    ...accounts,
    document,
    reportAddressLookup,
    reportAdverseMedia,
    reportBusinessAdverseMedia,
    reportBusinessAssociatedPersons,
    reportBusinessClassification,
    reportBusinessIndustryClassification,
    reportBusinessLiensLookup,
    reportBusinessLookup,
    reportBusinessNonprofit,
    reportBusinessOnlinePresence,
    reportBusinessRegistrationsLookup,
    reportBusinessSocialMedia,
    reportBusinessWatchlist,
    reportBusinessWebsite,
    reportChainalysisAddressScreening,
    reportCryptoAddressWatchlist,
    reportEmailAddress,
    reportFinraBrokerCheck,
    reportMxAccount,
    reportPhoneNumber,
    reportPoliticallyExposedPerson,
    reportProfile,
    reportProfileNonAuthoritative,
    reportCustomList,
    reportRunCustomList,
    reportSecActionLookup,
    reportSentilinkApplicationRisk,
    reportSentilinkScores,
    reportSocialMedia,
    reportSynthetic,
    reportWatchlist,
  ];

  const devices = [device];

  const caseDataManagerFields: Record<string, any> = {};
  Object.keys(filledFields).forEach(
    (fieldKey) => (caseDataManagerFields[fieldKey] = filledFields[fieldKey]?.value),
  );
  const caseDataManager = {
    'current-user': {},
    case: {
      fields: caseDataManagerFields,
    },
  };

  const handleToggleChecklistItem = (itemId: string) => {
    const itemIdx = kase.attributes['checklist-items'].findIndex((item) => item.token === itemId);
    if (itemIdx !== -1) {
      const item = kase.attributes['checklist-items'][itemIdx];
      item['completed-at'] = new Date().toISOString();
    }
  };

  // NOTE: since the event timeline uses ConnectedEventTimeline, we don't actually
  // get to see any events in this preview. We would probably have to make a separate
  // component for the event timeline preview if we want to show fake events.
  // Additionally, we want to memoize this module because the ConnectedEventTimeline
  // will make network calls to try to load events each time it is recalculated.
  const eventTimelineModule = useMemo(() => {
    if (module.type === ModuleTypes.EventTimeline) {
      return (
        <EventTimeline
          module={module}
          subject={kase}
          objects={[]}
          currentEnvironment={currentEnvironment}
          isPreview={true}
        />
      );
    } else {
      return null;
    }
  }, [kase, module]);

  if (module.type === ModuleTypes.EventTimeline) {
    return eventTimelineModule;
  }

  if (module.type === ModuleTypes.RelatedCases) {
    const relatedCases = [
      createPreviewCase({ name: 'REL-1' }, { accounts: { data: [legacyUserAccount] } }, 'case_01'),
      createPreviewCase({ name: 'REL-2' }, { accounts: { data: [legacyUserAccount] } }, 'case_02'),
    ];

    return (
      <PureRelatedCasesModule
        hiddenCase={kase}
        accounts={[legacyUserAccount]}
        users={[user]}
        relatedCases={relatedCases}
      />
    );
  }

  if (module.type === ModuleTypes.Table) {
    return <TablePreview module={module} />;
  }

  return (
    <CaseModuleBase
      module={module}
      caseApi={{
        assignee: user,
        case: kase,
        caseActionWorkflows: [],
        caseComments,
        caseMessages: [],
        caseObjects,
        caseQueryResults: [],
        caseTemplateStates,
        caseTemplateFieldSchema: fieldSchema,
        caseEmailTemplates: [],
        caseCommentTemplates: [],
        currentEnvironment,
        devices,
        documents: [document],
        featureFlags: {},
        inquiryTemplates: inquiryTemplates,
        inquiryTemplateVersions: [],
        inquiryVerifications: [verificationGovId, verificationSelfie],
        verifications: verifications,
        lists: [],
        listItems: [],
        listMatches: [],
        reportTemplates: [],
        sarNarrativeTemplates: [],
        sessions,
        tags: allTags,
        transactionStatuses,
        transactionTypes,
        allInquiryTemplates: [],
        users: [user],
        commentWorkflows: [],
        commentWorkflowStepActions: [],
        commentWorkflowRuns: [],
        inquiries: [],
        accounts: accounts,
        accountStatuses,
        accountTypes,
        isLoadingInquiriesDetails: false,
        isLoadingReportsDetails: false,
        // TODO: create a preview version of this.
        caseDataManager: caseDataManager,
        addComment: () => new Promise(noop),
        editCaseComment: () => new Promise(noop),
        removeCaseComment: () => new Promise(noop),
        handleUpdateTags: noop,
        handleUpdateInquiryTags: noop,
        handleUpdateReportTags: noop,
        handleAddTags: noop,
        handleAddCaseAttachment: noop,
        handleDeleteCaseAttachment: noop,
        handleToggleChecklistItem: handleToggleChecklistItem,
        handleBulkUpdateAccountStatus: noop,
        handleRefreshCaseQueryResult: () => new Promise(noop),
        handleRunCaseActionWorkflow: () => new Promise(noop),
        handleCreateEmailDraft: () => new Promise(noop),
        handleUpdateEmailDraft: () => new Promise(noop),
        handleSendEmail: () => new Promise(noop),
        handleDeleteEmail: () => new Promise(noop),
        handleCreateSAR: () => new Promise(noop),
        handleUpdateSAR: () => new Promise(noop),
        handleAutofillSAR: () => new Promise(noop),
        handleFlagInquiryIssues: () => new Promise(noop),
        handleFlagVerificationIssues: () => new Promise(noop),
        handleUpdateFields: noop,
        handleGenerateSummary: () => new Promise(noop),
        showMessage: noop,
      }}
      runCodeWithContext={async () => noop}
      isPreview
    />
  );
};

const MemoizedCaseModuleBasePreview = memo(CaseModuleBasePreview, (prevProps, nextProps) => {
  // We want to compare all props except the position data on the module so we don't rerender when modules move.
  // This is a short-term fix until we can split out positioning data from the module.
  const {
    fieldSchema: prevFieldSchema,
    checklistItems: prevChecklistItems,
    caseTemplateStates: prevCaseTemplateStates,
    caseVariableDefinitions: prevCaseVariableDefinitions,
    module: { position: prevModulePosition, ...restPrevModule },
  } = prevProps;
  const {
    fieldSchema: nextFieldSchema,
    checklistItems: nextChecklistItems,
    caseTemplateStates: nextCaseTemplateStates,
    caseVariableDefinitions: nextCaseVariableDefinitions,
    module: { position: nextModulePosition, ...restNextModule },
  } = nextProps;

  const isPropsEqual =
    isEqual(nextFieldSchema, prevFieldSchema) &&
    isEqual(nextChecklistItems, prevChecklistItems) &&
    isEqual(nextCaseTemplateStates, prevCaseTemplateStates) &&
    isEqual(nextCaseVariableDefinitions, prevCaseVariableDefinitions) &&
    isEqual(restPrevModule, restNextModule);

  return isPropsEqual;
});

// Keep up to date with FieldSchemaType
export const getFilledFields = (fieldSchema: FieldSchema[]) => {
  return fieldSchema.reduce((filledFields, schema) => {
    // convert field schema keys to kebab case
    const key = schema.key.split('_').join('-');
    let value;

    switch (schema.type) {
      case FieldSchemaType.Array: {
        value = {
          type: FieldType.Array,
          value: [],
        };
        break;
      }
      case FieldSchemaType.Boolean: {
        value = {
          type: FieldType.Boolean,
          value: false,
        };
        break;
      }
      case FieldSchemaType.Choices: {
        value = {
          type: FieldType.Choices,
          value: schema.config.options?.[0],
        };
        break;
      }
      case FieldSchemaType.Date: {
        value = {
          type: FieldType.Date,
          value: today(Settings.defaultZone.name).toString(),
        };
        break;
      }
      case FieldSchemaType.Datetime: {
        value = {
          type: FieldType.Datetime,
          value: formatDate(new Date().toISOString(), DATE_AND_TIME_TZ_FORMAT),
        };
        break;
      }
      case FieldSchemaType.File: {
        value = {
          type: FieldType.File,
          value: null,
        };
        break;
      }
      case FieldSchemaType.Hash: {
        value = {
          type: FieldType.Hash,
          value: {},
        };
        break;
      }
      case FieldSchemaType.Integer: {
        const min = schema.config.min;
        const max = schema.config.max;
        value = { type: FieldType.Integer, value: min ? min : max ? max : 10 };
        break;
      }
      case FieldSchemaType.Json: {
        value = { type: FieldType.Json, value: { key: 'value' } };
        break;
      }
      case FieldSchemaType.MultiChoices: {
        value = { type: FieldType.MultiChoices, value: {} };
        break;
      }
      case FieldSchemaType.Number: {
        const min = schema.config.min;
        const max = schema.config.max;
        value = { type: FieldType.Number, value: min ? min : max ? max : 1 };
        break;
      }
      case FieldSchemaType.String: {
        value = { type: FieldType.String, value: `${schema.key} value` };
        break;
      }
      case FieldSchemaType.DocumentGeneric: {
        value = { type: FieldType.DocumentGeneric, value: null };
        break;
      }
      case FieldSchemaType.GovernmentId: {
        value = { type: FieldType.GovernmentId, value: null };
        break;
      }
      case FieldSchemaType.GovernmentIdNfc: {
        value = { type: FieldType.DocumentGovernmentIdNfc, value: null };
        break;
      }
      case FieldSchemaType.Selfie: {
        value = { type: FieldType.Selfie, value: null };
        break;
      }
      default:
        break;
    }
    return {
      [key]: value,
      ...filledFields,
    };
  }, {});
};

export default MemoizedCaseModuleBasePreview;
